import React from 'react'
import { useTranslation } from 'react-i18next'
import { User, Users } from 'lucide-react'
import { formatNumber } from '../../../utils/formatters'
import ProfileLink from '../../../components/ProfileLink'
import TeamTag from '../../../components/TeamTag'
import useAppStore from '../../../store/useAppStore'

const RANK_STYLES = {
  1: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40',
  2: 'bg-slate-300/15 text-slate-200 border-slate-300/30',
  3: 'bg-amber-700/20 text-amber-500 border-amber-600/40'
}

/**
 * Топ-5 недельного розыгрыша + своя строка снизу.
 * Одинаков в announce (живые данные) и results (снапшот на момент жеребьёвки).
 * chat_id в ответе нет: своя строка в топе подсвечивается по флагу you === 1,
 * отдельная строка под топом рисуется только при in_top5 === false.
 * Неучастнику (is_participant === false) место не показывается вовсе.
 */
const RushDropWeeklyTop5 = ({ top5, you, participants }) => {
  const { t } = useTranslation()
  const isGuest = useAppStore((state) => state.isGuest)
  const openLogin = useAppStore((state) => state.openLogin)

  const rows = Array.isArray(top5) ? top5 : []
  const participantsCount = Number(participants || 0)
  const isParticipant = Boolean(you?.is_participant)
  const showOwnRow = isParticipant && you?.in_top5 === false

  const formatChance = (chance) =>
    `${Number(chance || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`

  const renderAvatar = (entry) => {
    if (entry?.avatar) {
      return (
        <img
          src={entry.avatar}
          alt=""
          className="h-8 w-8 rounded-full border border-white/15 object-cover"
          loading="lazy"
          onError={(e) => {
            // Фолбэк при ошибке загрузки аватарки
            e.currentTarget.style.display = 'none'
          }}
        />
      )
    }
    return (
      <div className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-white/5">
        <User size={14} className="text-text-secondary" />
      </div>
    )
  }

  const renderRow = (entry, key) => (
    <div
      key={key}
      className={`flex items-center gap-3 rounded-xl border px-3 py-2 ${
        Number(entry?.you) === 1
          ? 'border-accent-primary/40 bg-accent-primary/10'
          : 'border-white/5 bg-white/[0.02]'
      }`}
    >
      <span
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border text-xs font-bold tabular-nums ${
          RANK_STYLES[entry?.place] || 'border-white/10 bg-white/5 text-text-secondary'
        }`}
      >
        {entry?.place}
      </span>
      {/* Аватар кликабелен: slug === null → игрок не кликабелен */}
      <ProfileLink slug={entry?.slug} className="shrink-0">
        {renderAvatar(entry)}
      </ProfileLink>
      <p className="min-w-0 flex-1 truncate text-sm font-medium text-white">
        <TeamTag team={entry?.team} className="" />
        <ProfileLink slug={entry?.slug}>
          {entry?.name || t('rushdrop.leaderboard.player', 'Игрок')}
        </ProfileLink>
      </p>
      <span className="shrink-0 text-xs tabular-nums text-text-secondary">
        {t('rushdrop.weeklyDraw.chance', 'Шанс')}{' '}
        <b className="text-yellow-300">{formatChance(entry?.chance)}</b>
      </span>
      <span className="shrink-0 text-sm font-semibold tabular-nums text-text-secondary">
        {formatNumber(Number(entry?.points || 0))}
      </span>
    </div>
  )

  return (
    <section>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-lg font-bold text-white">
          <Users size={18} className="text-yellow-400" />
          {t('rushdrop.weeklyDraw.topTitle', 'Топ-5 недели')}
        </h2>
        <span className="text-xs text-text-secondary">
          {t('rushdrop.leaderboard.participants', 'Участников: {{count}}', {
            count: formatNumber(participantsCount)
          })}
        </span>
      </div>

      <div className="space-y-2">
        {rows.map((entry) => renderRow(entry, entry?.place ?? entry?.name))}
        {rows.length === 0 && (
          <p className="rounded-xl border border-white/5 bg-white/[0.02] px-3 py-3 text-center text-xs text-text-secondary">
            {t('rushdrop.weeklyDraw.emptyTop', 'За неделю никто не набрал баллов')}
          </p>
        )}
      </div>

      {/* Своя строка: под топом — только если сам не в пятёрке */}
      <div className="mt-3 border-t border-white/10 pt-3">
        {isGuest ? (
          <button type="button" onClick={openLogin} className="w-full rounded-xl bg-accent-primary px-4 py-2.5 text-sm font-bold text-dark-primary">{t('guest.login')}</button>
        ) : showOwnRow ? (
          <div className="flex items-center justify-between gap-2 rounded-xl border border-accent-primary/30 bg-accent-primary/5 px-3 py-2 text-sm">
            <span className="font-medium text-white">
              {t('rushdrop.weeklyDraw.you', 'Ты')}: {t('rushdrop.weeklyDraw.place', 'Место')}{' '}
              <b className="tabular-nums">{you.place}</b>
            </span>
            <span className="text-xs tabular-nums text-text-secondary">
              {formatNumber(Number(you.points || 0))} · {t('rushdrop.weeklyDraw.chance', 'Шанс')}{' '}
              <b className="text-accent-primary">{formatChance(you.chance)}</b>
            </span>
          </div>
        ) : !isParticipant ? (
          <div className="rounded-xl border border-white/5 bg-white/[0.02] px-3 py-3 text-center">
            <p className="text-xs font-medium text-white">
              {t('rushdrop.weeklyDraw.notParticipant', 'У тебя 0 баллов — ты не участвуешь в этом розыгрыше')}
            </p>
            <p className="mt-1 text-xs text-text-secondary">
              {t('rushdrop.weeklyDraw.notParticipantHint', 'Закрой все задания любого Rush Drop — и попадёшь в следующий розыгрыш')}
            </p>
          </div>
        ) : null}
      </div>
    </section>
  )
}

export default RushDropWeeklyTop5
